import {
  CheckOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  LinkOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  ApolloCache,
  NormalizedCacheObject,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  BaselineItem,
  CREATE_IDEA_METRIC,
  DELETE_IDEA_METRIC,
  ErrorResponse,
  GET_CHANNEL_IDEA,
  GET_MY_IDEAS,
  Idea,
  LIST_BASELINES,
  LIST_METRIC_UNITS,
  LIST_METRICS,
  LIST_PROJECTS,
  MetricItem,
  MetricUnit,
  MetricUnitType,
  Project,
  UPDATE_IDEA_METRIC,
} from '@frontend/data-access';
import { selectChannel, setChannel } from '@frontend/feature-idea-channel';
import {
  App,
  Button,
  Divider,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../channel-store/settings/hooks';

interface MetricForm {
  id: string;
  metric?: string;
  project?: string | null;
  targetAmount?: string;
  baselineData?: string;
  actualAmount?: string;
  unit?: string;
  unitType?: MetricUnitType;
}

interface MetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIdea: Idea;
  platformType: 'admin' | 'platform';
}

export const IdeaChannelMetrics = ({
  isOpen,
  onClose,
  selectedIdea,
  platformType,
}: MetricsModalProps) => {
  const { notification } = App.useApp();
  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectChannel);

  const [showMetricForm, setShowMetricForm] = useState(false);
  const [metricForms, setMetricForms] = useState<MetricForm[]>([]);
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const [showBaselineData, setShowBaselineData] = useState(false);
  const [hasChanges, setHasChanges] = useState<{ [key: string]: boolean }>({});
  const { t: tcommon } = useTranslation('common');

  const { refetch } = useQuery<{
    me: { id: number; ideas: { items: Idea[]; total: number } };
  }>(GET_MY_IDEAS, { variables: { page: 1, limit: 5 } });

  const { data: metricsData } = useQuery<{ listMetrics: MetricItem[] }>(
    LIST_METRICS
  );
  const { data: unitsData } = useQuery<{ listMetricUnits: MetricUnit[] }>(
    LIST_METRIC_UNITS
  );
  const { data: projectsData } = useQuery<{ listProjects: Project[] }>(
    LIST_PROJECTS
  );
  const { data: baselineData } = useQuery<{ listBaseline: BaselineItem[] }>(
    LIST_BASELINES
  );

  const projectOptions = useMemo(() => {
    const options =
      projectsData?.listProjects?.map((project) => ({
        value: project.id,
        label: project.name,
      })) ?? [];
    return [{ value: null, label: 'No Project' }, ...options];
  }, [projectsData]);

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: tcommon('update_error'),
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const handleUpdateCache = (
    cache: ApolloCache<NormalizedCacheObject>,
    {
      data,
    }: {
      data: {
        createIdeaMetric: {
          id: string;
          idea: Idea;
        };
        patchIdeaMetric: {
          id: string;
          idea: Idea;
        };
      };
    }
  ) => {
    const { patchIdeaMetric, createIdeaMetric } = data || {};
    const updatedIdea = patchIdeaMetric?.idea || createIdeaMetric?.idea;
    if (channel && updatedIdea) {
      const updatedChannel = {
        ...channel,
        channelPhases: channel.channelPhases.map((channelPhase) => ({
          ...channelPhase,
          ideas: {
            ...channelPhase.ideas,
            items: channelPhase.ideas.items.map((idea) =>
              idea.id === updatedIdea.id
                ? {
                    ...idea,
                    metricTag: updatedIdea.metricTag,
                  }
                : idea
            ),
          },
        })),
      };
      dispatch(setChannel({ ...updatedChannel }));
    }
  };

  const [createIdeaMetric, { loading: createLoading }] = useMutation(
    CREATE_IDEA_METRIC,
    {
      onCompleted() {
        notification.success({
          message: 'Metric added successfully',
        });
        setActiveMetric(null);
        refetch();
      },
      update: handleUpdateCache as any,
      onError: handleError,
      refetchQueries: [
        { query: GET_CHANNEL_IDEA, variables: { ideaId: selectedIdea?.id } },
        { query: GET_MY_IDEAS },
      ],
    }
  );

  const [deleteIdeaMetric, { loading: deleteLoading }] = useMutation(
    DELETE_IDEA_METRIC,
    {
      onCompleted() {
        notification.success({
          message: 'Metric deleted successfully',
        });
        setActiveMetric(null);
        refetch();
      },
      onError: handleError,
      refetchQueries: [
        { query: GET_CHANNEL_IDEA, variables: { ideaId: selectedIdea?.id } },
        { query: GET_MY_IDEAS },
      ],
    }
  );

  const [updateIdeaMetric, { loading: updateLoading }] = useMutation(
    UPDATE_IDEA_METRIC,
    {
      onCompleted() {
        notification.success({
          message: 'Metric updated successfully',
        });
        setActiveMetric(null);
      },
      update: handleUpdateCache as any,
      onError: handleError,
      refetchQueries: [
        { query: GET_CHANNEL_IDEA, variables: { ideaId: selectedIdea?.id } },
        { query: GET_MY_IDEAS },
      ],
    }
  );

  useEffect(() => {
    if (!isOpen) return;
    if (selectedIdea?.metrics?.length) {
      setShowMetricForm(true);
      setMetricForms(
        selectedIdea.metrics.map((metric, index) => ({
          index: index,
          id: metric.id,
          metric: metric.metric.id,
          project: metric.project?.id || null,
          targetAmount: metric.target?.toString(),
          actualAmount: metric.result?.toString(),
          unit: metric.metric.unit?.id,
          unitType: metric.metric.unit?.type,
        }))
      );
    } else {
      setShowMetricForm(false);
      setMetricForms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedIdea]);

  const getBaselineData = (metricId?: string, projectId?: string | null) => {
    return baselineData?.listBaseline?.find(
      (item) =>
        item.metric?.id === metricId &&
        (item.project?.id === projectId || (!item.project?.id && !projectId))
    );
  };

  const getUnitLabel = (unitId?: string) => {
    return unitsData?.listMetricUnits?.find((unit) => unit.id === unitId)
      ?.label;
  };

  const handleAddNewMetric = () => {
    setShowMetricForm(true);
    setMetricForms([{ id: '1' }]);
  };

  const handleAddMoreMetric = () => {
    setMetricForms((prev) => [...prev, { id: String(prev.length + 1) }]);
  };

  const handleMetricChange = (id: string, value: string) => {
    const selectedMetric = metricsData?.listMetrics?.find(
      (metric) => metric.id === value
    );

    setMetricForms((prev) =>
      prev.map((form) =>
        form.id === id
          ? {
              ...form,
              metric: value,
              unit: selectedMetric?.unit?.id,
              unitType: selectedMetric?.unit?.type,
            }
          : form
      )
    );
    setHasChanges((prev) => ({ ...prev, [id]: true }));
  };

  const handleProjectChange = (id: string, value: string) => {
    setMetricForms((prev) =>
      prev.map((form) => (form.id === id ? { ...form, project: value } : form))
    );
    setHasChanges((prev) => ({ ...prev, [id]: true }));
  };

  const handleTargetAmountChange = (
    id: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setMetricForms((prev) =>
      prev.map((form) =>
        form.id === id ? { ...form, targetAmount: value } : form
      )
    );
    setHasChanges((prev) => ({ ...prev, [id]: true }));
  };

  const handleActualAmountChange = (
    id: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setMetricForms((prev) =>
      prev.map((form) =>
        form.id === id ? { ...form, actualAmount: value } : form
      )
    );
    setHasChanges((prev) => ({ ...prev, [id]: true }));
  };

  const toggleShowBaselineData = (checked: boolean) => {
    setShowBaselineData(checked);
  };

  const handleDeleteRow = async (id: string) => {
    const metricToDelete = selectedIdea?.metrics?.find(
      (metric) => metric.id === id
    );

    if (metricToDelete?.id) {
      setActiveMetric(id);
      try {
        await deleteIdeaMetric({
          variables: {
            id: metricToDelete.id,
          },
        });
      } catch (error) {
        notification.error({
          message: 'Failed to delete metric',
        });
        setActiveMetric(null);
      }
    }

    setMetricForms((prev) => prev.filter((form) => form.id !== id));
    if (metricForms.length <= 1) {
      setShowMetricForm(false);
    }
  };

  const handleSubmitMetric = (index: number) => {
    if (
      !selectedIdea ||
      !metricForms?.length ||
      !metricForms[index - 1].metric
    ) {
      console.log(
        `no selectedIdea ${!selectedIdea} or metricForms ${!metricForms?.length} or metricForms[index - 1].metric ${!metricForms[
          index - 1
        ].metric}`
      );
      return;
    }

    const form = metricForms[index - 1];
    const existingMetric = selectedIdea.metrics?.find(
      (metric) => metric.id === form.id
    );
    setActiveMetric(form.id);

    if (existingMetric?.id) {
      updateIdeaMetric({
        variables: {
          input: {
            id: existingMetric.id,
            projectId: form.project,
            target: form.targetAmount,
            result: form.actualAmount,
          },
        },
      });
    } else {
      createIdeaMetric({
        variables: {
          input: {
            ideaId: selectedIdea.id,
            metricId: form.metric,
            projectId: form.project,
            target: form.targetAmount,
            result: form.actualAmount,
          },
        },
      });
    }

    setHasChanges((prev) => ({ ...prev, [form.id]: false }));
  };

  const getStatus = useCallback(() => {
    let text = tcommon('no_metrics');
    let color = 'bg-red-100 text-red-600';

    switch (selectedIdea?.metricTag) {
      case 'NONE':
        text = tcommon('no_metrics');
        color = 'bg-gray-300';
        break;
      case 'NO_TARGET':
        text = tcommon('no_target_metrics');
        color = 'bg-red-100 text-red-600';
        break;
      case 'TARGET':
        text = tcommon('target_metrics');
        color = 'bg-blue-100 text-blue-600';
        break;
      case 'RESULT':
        text = tcommon('actual_result');
        color = 'bg-green-100 text-green-600';
        break;
    }
    return (
      <div className="space-y-2">
        <div className="text-gray-700">{tcommon('metric_status')}</div>
        <div className={`text-xs ${color} px-2 py-1 rounded-full text-center`}>
          {text}
        </div>
      </div>
    );
  }, [selectedIdea?.metricTag, tcommon]);

  const handleClose = () => {
    setShowMetricForm(false);
    setMetricForms([]);
    setActiveMetric(null);
    setShowBaselineData(false);
    setHasChanges({});

    onClose();
  };

  return (
    <Modal
      title={tcommon('add_idea_metrics')}
      open={isOpen}
      onCancel={handleClose}
      footer={
        <div className="flex items-center space-x-2">
          <Switch onChange={toggleShowBaselineData} value={showBaselineData} />
          <span>{tcommon('show_baseline_data')}</span>
          <Tooltip title={tcommon('baseline_data_tooltip')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      }
      width={1000}
    >
      <div className="flex items-center justify-between space-x-4">
        <div>
          <div className="text-gray-700">{tcommon('idea_name')}:</div>
          <div className="font-medium text-primary-800 flex items-center space-x-2">
            <LinkOutlined />
            <Link
              href={
                platformType === 'admin'
                  ? `/idea-management/${selectedIdea?.id}`
                  : `/idea/${selectedIdea?.id}`
              }
            >
              {selectedIdea?.name}
            </Link>
          </div>
        </div>
        {showMetricForm && getStatus()}
      </div>

      <Divider />

      {!showMetricForm ? (
        <div className="text-center">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<p>{tcommon('measure_idea_impact')}</p>}
          />
          <Button
            shape="round"
            onClick={handleAddNewMetric}
            data-test="add-new-metric-btn"
          >
            <PlusOutlined />
            {tcommon('add_new_metric')}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-end space-x-4 overflow-x-auto">
            <div className="w-[180px] font-medium text-gray-700 flex items-center gap-2">
              {tcommon('metric')}
              <Tooltip title={tcommon('metric_tooltip')}>
                <QuestionCircleOutlined />
              </Tooltip>
            </div>
            <div className="w-[180px] font-medium text-gray-700 flex items-center gap-2">
              {tcommon('project')}
              <Tooltip title={tcommon('project_tooltip')}>
                <QuestionCircleOutlined />
              </Tooltip>
            </div>
            <div className="w-[180px] font-medium text-gray-700 flex items-center gap-2">
              {tcommon('target_amount')}
              <Tooltip title={tcommon('target_amount_tooltip')}>
                <QuestionCircleOutlined />
              </Tooltip>
            </div>
            <div className="w-[180px] font-medium text-gray-700 flex items-center gap-2">
              {tcommon('actual_result')}
              <Tooltip title={tcommon('actual_result_tooltip')}>
                <QuestionCircleOutlined />
              </Tooltip>
            </div>
            <div className="w-[90px]"></div>
          </div>

          {metricForms.map((form, idx) => {
            const isQualitative = form.unitType === MetricUnitType.QUALITATIVE;
            const isQuantitative =
              form.unitType === MetricUnitType.QUANTITATIVE;
            return (
              <div
                key={form.id}
                className="flex items-start space-x-4 overflow-x-auto"
                data-test="metric-form-row"
              >
                <div className="w-[180px]">
                  <Select
                    data-test="metric"
                    placeholder={tcommon('select')}
                    showSearch
                    className="w-full"
                    popupClassName="!w-[300px]"
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    value={form.metric}
                    onChange={(value) => handleMetricChange(form.id, value)}
                    options={metricsData?.listMetrics?.map((item) => {
                      const isQualitative =
                        item.unit?.type === MetricUnitType.QUALITATIVE;
                      return {
                        value: item.id,
                        label:
                          item.name +
                          (isQualitative ? '' : ` (${item.unit?.label})`),
                      };
                    })}
                    optionRender={(item) => (
                      <div>
                        <div className="font-medium text-gray-900">
                          {item.label}
                        </div>
                        <div className="text-gray-700 line-clamp-3 whitespace-pre-wrap text-sm">
                          {
                            metricsData?.listMetrics?.find(
                              (m) => m.id === item.value
                            )?.description
                          }
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="w-[180px]">
                  <Select
                    data-test="project"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    className="w-full"
                    placeholder={tcommon('select')}
                    disabled={!form.metric}
                    value={form.project}
                    onChange={(value) => handleProjectChange(form.id, value)}
                    options={projectOptions}
                  />
                </div>

                <div className="w-[180px] flex flex-col gap-2">
                  <Input
                    data-test="target"
                    type="text"
                    placeholder={
                      isQualitative
                        ? tcommon('success_means')
                        : tcommon('enter_amount')
                    }
                    suffix={isQuantitative && getUnitLabel(form.unit)}
                    value={form.targetAmount?.toString()}
                    onChange={(e) => handleTargetAmountChange(form.id, e)}
                  />
                  {showBaselineData &&
                    getBaselineData(form.metric, form.project) && (
                      <div className="text-xs text-gray-500">
                        {tcommon('idea_metric_baseline_data', {
                          baseline: getBaselineData(form.metric, form.project)
                            ?.baseline,
                          unit: isQuantitative ? getUnitLabel(form.unit) : '',
                        })}
                      </div>
                    )}
                </div>

                <div className="w-[180px]">
                  <Input
                    data-test="result"
                    placeholder={
                      isQualitative
                        ? tcommon('success_means')
                        : tcommon('enter_amount')
                    }
                    suffix={isQuantitative && getUnitLabel(form.unit)}
                    disabled={
                      !selectedIdea?.metrics?.find(
                        (metric) => metric.id === form.id
                      )?.id
                    }
                    value={form.actualAmount}
                    onChange={(e) => handleActualAmountChange(form.id, e)}
                  />
                </div>

                <div className="flex items-end space-x-2">
                  <Button
                    shape="circle"
                    icon={<CheckOutlined />}
                    disabled={
                      createLoading || updateLoading || !hasChanges[form.id]
                    }
                    onClick={() => handleSubmitMetric(idx + 1)}
                    loading={
                      activeMetric === form.id &&
                      (createLoading || updateLoading)
                    }
                    data-test="metric-save-btn"
                  />
                  <Popconfirm
                    title={tcommon('confirm_delete_metric')}
                    onConfirm={() => handleDeleteRow(form.id)}
                    okText={tcommon('yes')}
                    cancelText={tcommon('no')}
                  >
                    <Button
                      danger
                      shape="circle"
                      icon={<DeleteOutlined />}
                      loading={activeMetric === form.id && deleteLoading}
                    />
                  </Popconfirm>
                </div>
              </div>
            );
          })}

          <Button
            shape="round"
            onClick={handleAddMoreMetric}
            data-test="add-new-metric-btn"
          >
            <PlusOutlined /> {tcommon('add_more_metric')}
          </Button>
        </div>
      )}
    </Modal>
  );
};
